import React, { useMemo, useCallback } from 'react';
import { PaginationInfo } from './PaginationInfo';
import { PaginationControls } from './PaginationControls';
import { RowsPerPageSelect } from './RowsPerPageSelect';
import { calculatePaginationValues } from './pagination.utils';

// Extracted base classes to avoid recreation on every render
const BASE_CONTAINER_CLASSES = `
  flex items-center justify-start
  w-full py-4 px-6
  bg-white border-t border-gray-200
  font-roboto
  flex-col gap-4 sm:flex-row sm:gap-0
`.trim();

export interface PaginationProps {
  className?: string;
  // State
  currentPage: number;
  disabled?: boolean;

  rowsPerPage: number;
  // Optional configuration
  rowsPerPageOptions?: number[];

  showInfo?: boolean;
  testId?: string;
  totalItems: number;
  // Event handlers
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  rowsPerPage,
  totalItems,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 20, 30],
  disabled = false,
  className = '',
  testId = 'pagination',
  showInfo = true,
}) => {
  // Don't render if no items
  if (totalItems === 0) {
    return null;
  }

  // Calculate pagination values using utility function - memoized
  const { totalPages, startIndex, endIndex, hasNextPage, hasPrevPage } = useMemo(
    () => calculatePaginationValues(currentPage, rowsPerPage, totalItems),
    [currentPage, rowsPerPage, totalItems]
  );

  // Memoized handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage < 1 || newPage > totalPages) {
        return;
      }
      onPageChange(newPage);
    },
    [totalPages, onPageChange]
  );

  const handleNextPage = useCallback(() => {
    if (hasNextPage) {
      handlePageChange(currentPage + 1);
    }
  }, [hasNextPage, currentPage, handlePageChange]);

  const handlePrevPage = useCallback(() => {
    if (hasPrevPage) {
      handlePageChange(currentPage - 1);
    }
  }, [hasPrevPage, currentPage, handlePageChange]);

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      onRowsPerPageChange(newRowsPerPage);

      // Reset to page 1 if current page exceeds new total pages
      const newTotalPages = Math.ceil(totalItems / newRowsPerPage);
      if (currentPage > newTotalPages) {
        onPageChange(1);
      }
    },
    [totalItems, currentPage, onPageChange, onRowsPerPageChange]
  );

  // Memoized container classes
  const containerClasses = useMemo(
    () => (className ? `${BASE_CONTAINER_CLASSES} ${className}` : BASE_CONTAINER_CLASSES),
    [className]
  );

  return (
    <nav
      role="navigation"
      aria-label="Pagination navigation"
      data-testid={testId}
      className={containerClasses}
    >
      {/* Info section - only render if showing info */}
      {showInfo && (
        <div className="flex items-center gap-4">
          <PaginationInfo
            startIndex={startIndex}
            endIndex={endIndex}
            totalItems={totalItems}
            testId={`${testId}-info`}
          />
          {(hasNextPage || hasPrevPage) &&
            <PaginationControls
              hasNext={hasNextPage}
              hasPrev={hasPrevPage}
              onNext={handleNextPage}
              onPrev={handlePrevPage}
              disabled={disabled}
              testId={`${testId}-controls`}
            />}
        </div>
      )}

      {/* Controls section */}
      {/* {(hasNextPage || hasPrevPage) && <div className="flex items-center gap-4">
        <PaginationControls
          hasNext={hasNextPage}
          hasPrev={hasPrevPage}
          onNext={handleNextPage}
          onPrev={handlePrevPage}
          disabled={disabled}
          testId={`${testId}-controls`}
        />

        {false && <RowsPerPageSelect
          value={rowsPerPage}
          options={rowsPerPageOptions}
          onChange={handleRowsPerPageChange}
          disabled={disabled}
          testId={`${testId}-rows-per-page`}
        />}
      </div>} */}
    </nav>
  );
};
